import React, { useState, useRef, forwardRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ThemeMoonIcon, ThemeSunIcon } from "../../widgets/icons";
import { defaultPfpUrl, standardThemes, THEME_SYSTEM, getThemeLabel, isLightThemeClass } from "../utils";
import { logoutUser } from "../../api/api";
import { BrandLogo } from "./BrandLogo";
import { GlobalSearch } from "./GlobalSearch";
import HeaderDropdownPortal from "./HeaderDropdownPortal";

const Header = forwardRef(function Header(
    { userId, user, setUserId, theme, setTheme, effectiveTheme },
    ref
) {

    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownAnchorRef = useRef(null);
    const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
    const themeAnchorRef = useRef(null);

    const themeMenuChoices = [
        ...standardThemes,
        { id: THEME_SYSTEM, label: getThemeLabel(THEME_SYSTEM) },
    ];

    // Calculate pending affiliations count
    const pendingAffiliationsCount = user?.affiliations?.filter(aff => aff.user_id === userId && aff.status === 'pending')?.length || 0;
    const affStr = pendingAffiliationsCount > 9 ? '9+' : pendingAffiliationsCount;

    function toProfilePage() {
        if (user.type === 'user') {
            navigate(`/profile/${userId}`);
        } else {
            navigate(`/organization/${userId}`);
        }
    }

    function toSettingsPage() {
        navigate(`/settings`);
        setIsDropdownOpen(false);
    }

    function toNotificationsPage() {
        navigate(`/notifications`);
        setIsDropdownOpen(false);
    }

    async function handleLogout() {
        await logoutUser();
        setUserId(0);
        navigate("/");
        setIsDropdownOpen(false);
    }

    return (
        <header
            ref={ref}
            className="fixed inset-x-0 top-0 z-40 max-w-full overflow-x-clip border-b border-ui-border bg-ui-card text-text-primary"
        >
            <div className="grid h-14 w-full max-w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1.5 px-2 sm:gap-3 sm:px-4">
                <div className="flex shrink-0 justify-self-start">
                    <BrandLogo />
                </div>

                <div className="flex min-w-0 justify-center overflow-hidden">
                    <GlobalSearch className="w-full max-w-xl" />
                </div>

                <div className="flex shrink-0 items-center justify-end justify-self-end gap-x-0.5 sm:gap-x-3">
                <div className="hidden md:block" ref={themeAnchorRef}>
                    <button
                        type="button"
                        aria-haspopup="listbox"
                        aria-expanded={isThemeMenuOpen}
                        aria-label="Theme"
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-text-primary transition hover:bg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background"
                        onClick={() => setIsThemeMenuOpen((open) => !open)}
                    >
                        {isLightThemeClass(effectiveTheme) ? <ThemeSunIcon /> : <ThemeMoonIcon />}
                    </button>
                    <HeaderDropdownPortal
                        open={isThemeMenuOpen}
                        anchorRef={themeAnchorRef}
                        onClose={() => setIsThemeMenuOpen(false)}
                        role="listbox"
                        aria-label="Theme"
                        className="min-w-[10rem] rounded-md border border-border bg-secondary py-1 shadow-lg"
                    >
                        {themeMenuChoices.map((option) => {
                            const selected = theme === option.id;
                            return (
                                <button
                                    key={option.id}
                                    type="button"
                                    role="option"
                                    aria-selected={selected}
                                    className="flex w-full items-center justify-between gap-4 px-4 py-2 text-left text-sm font-medium text-text-primary hover:bg-tertiary"
                                    onClick={() => {
                                        setTheme(option.id);
                                        setIsThemeMenuOpen(false);
                                    }}
                                >
                                    <span>{option.label}</span>
                                    {selected ? (
                                        <span className="text-text-primary" aria-hidden>
                                            ✓
                                        </span>
                                    ) : (
                                        <span className="w-4 shrink-0" aria-hidden />
                                    )}
                                </button>
                            );
                        })}
                    </HeaderDropdownPortal>
                </div>

                {/* Auth Button */}
                {userId ? (
                    <div ref={dropdownAnchorRef}>
                        <button 
                            className="flex items-center focus:outline-none relative"
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            aria-haspopup="menu"
                            aria-expanded={isDropdownOpen}
                        >
                            <div className="relative">
                                <img
                                    src={user?.pfp_url || defaultPfpUrl}
                                    alt="Profile"
                                    className="w-8 h-8 rounded-full border-2 border-border"
                                />
                                {pendingAffiliationsCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-accent text-text-on-accent text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                        {affStr}
                                    </span>
                                )}
                            </div>
                        </button>
                        <HeaderDropdownPortal
                            open={isDropdownOpen}
                            anchorRef={dropdownAnchorRef}
                            onClose={() => setIsDropdownOpen(false)}
                            role="menu"
                            className="w-40 rounded bg-secondary text-accent shadow-lg"
                        >
                            <button
                                onClick={() => {
                                    toProfilePage();
                                    setIsDropdownOpen(false);
                                }}
                                className="block w-full text-left px-4 py-2 hover:bg-tertiary transition font-bold text-sm"
                            >
                                Profile
                            </button>
                            <button
                                onClick={toSettingsPage}
                                className="block w-full text-left px-4 py-2 hover:bg-tertiary transition font-bold text-sm"
                            >
                                Settings
                            </button>
                            <button
                                onClick={toNotificationsPage}
                                className="block w-full text-left px-4 py-2 hover:bg-tertiary transition font-bold text-sm flex items-center justify-between"
                            >
                                <span>Notifications</span>
                                {pendingAffiliationsCount > 0 && (
                                    <span className="text-xs text-text-on-accent bg-accent border border-accent rounded-full px-2 py-0.5 ml-2 flex-shrink-0">
                                        {affStr}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={handleLogout}
                                className="block w-full text-left px-4 py-2 hover:bg-tertiary transition font-bold text-sm"
                            >
                                Logout
                            </button>
                        </HeaderDropdownPortal>
                    </div>
                ) : (
                    <Link
                        to="/login"
                        className="bg-secondary text-accent border border-accent px-3 py-1.5 rounded hover:bg-tertiary transition font-bold text-sm sm:px-4 sm:py-2"
                    >
                        Login
                    </Link>
                )}
                </div>
            </div>
        </header>
    );
});

export default Header;
